import { Injectable } from '@angular/core';
import { API } from '@app/core/api';
import { FeatureState, Alert, HandledAlert, IFeatureState, IAlert, IHandledAlert } from '@app/models';
import { IAlertFlowStep } from '@app/models/alert-flow-step';
import { IAlertFullHistory } from '@app/models/alert-full-history';
import { IAlertHandlingRecord } from '@app/models/alert-handling';
import { GenericHttpClientService } from 'core-services';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AlertService extends GenericHttpClientService<IAlert | IFeatureState | IHandledAlert | string, string> {
    /**
     * read list of alerts from specified url
     * @param request the uri with params
     * @returns Observable of IAlert[]
     */
    readList(request: string): Observable<IAlert[]> {
        return super.readList(request)
                    .pipe(map((data) => adaptAlertList(data)));
    }

    /**
     * gets Active Alerts list
     * @returns Observable of FeatureState[]
     */
    getSubscriberFeatureStatesList(accountId?: string): Observable<FeatureState[]> {
        return super.readList(API.alertsApiUrl + accountId)
                    .pipe(map((data) => adaptSubscriberFeatureStatesList(data)));
    }

    /**
     * read list of handled alerts from specified url
     * @param request
     * @returns Observable of IHandledAlert[]
     */
    getHandledAlertsList(request: string): Observable<IHandledAlert[]> {
        return super.readList(request)
                    .pipe(map((data) => adaptHandledAlertList(data)));
    }
    /**
     * close alert
     * @returns Observable of FeatureState[]
     */
    closeAlert(alertID: string): Observable<any> {
        return super.update(API.closingAlertUrl, alertID);
    }
    /**
     * close alert
     * @returns Observable of FeatureState[]
     */
    closeAlerts(subscriberId: string): Observable<any> {
        return super.update(API.closingAlertsUrl, subscriberId);
    }

    /**
     * selects a flow for an alert, snapshotting its active steps
     * @param alertId the alert identifier
     * @param flowId the flow identifier
     * @returns Observable of IAlertFlowStep[]
     */
    selectFlowForAlert(alertId: string, flowId: string): Observable<IAlertFlowStep[]> {
        return super.create(`${API.flowsApiUrl}/alerts/${alertId}/select/${flowId}`, '') as unknown as Observable<IAlertFlowStep[]>;
    }

    /**
     * gets the current (latest) state of a flow's steps for an alert
     * @param alertId the alert identifier
     * @param flowId the flow identifier
     * @returns Observable of IAlertFlowStep[]
     */
    getCurrentAlertFlowSteps(alertId: string, flowId: string): Observable<IAlertFlowStep[]> {
        return super.readList(`${API.flowsApiUrl}/alerts/${alertId}/steps/${flowId}`) as unknown as Observable<IAlertFlowStep[]>;
    }

    /**
     * gets an alert's full history: every flow-step change across all its flows, alongside every
     * escalation/notification event
     * @param alertId the alert identifier
     * @returns Observable of IAlertFullHistory
     */
    getAlertFullHistory(alertId: string): Observable<IAlertFullHistory> {
        return super.readOne(`${API.flowsApiUrl}/alerts/${alertId}/full-history`) as unknown as Observable<IAlertFullHistory>;
    }

    /**
     * appends a status/free-text change for one step on an alert
     * @param alertId the alert identifier
     * @param flowId the flow identifier
     * @param flowStepId the flow step identifier
     * @param update the status/free-text update
     * @returns Observable of IAlertFlowStep
     */
    updateAlertFlowStep(alertId: string, flowId: string, flowStepId: string, update: { StatusCode: string; FreeText: string }): Observable<IAlertFlowStep> {
        return super.create(`${API.flowsApiUrl}/alerts/${alertId}/steps/${flowId}/${flowStepId}`, update as any) as unknown as Observable<IAlertFlowStep>;
    }

    /**
     * checks whether any flow has already been started for an alert
     * @param alertId the alert identifier
     * @returns Observable of boolean
     */
    hasStartedFlow(alertId: string): Observable<boolean> {
        return super.readOne(`${API.flowsApiUrl}/alerts/${alertId}/started`) as unknown as Observable<boolean>;
    }

    /**
     * gets the most recently active flow id started for an alert, if any
     * @param alertId the alert identifier
     * @returns Observable of string
     */
    getStartedFlowId(alertId: string): Observable<string> {
        return super.readOne(`${API.flowsApiUrl}/alerts/${alertId}/started-flow`) as unknown as Observable<string>;
    }

    /**
     * assigns an alert to the logged-in user
     * @param alertId the alert identifier
     * @returns Observable of IAlertHandlingRecord
     */
    assignAlertToMe(alertId: string): Observable<IAlertHandlingRecord> {
        return super.update(`${API.alertsBaseUrl}/${alertId}/handling/assign-to-me`, '' as any) as unknown as Observable<IAlertHandlingRecord>;
    }

    /**
     * assigns an alert to a specific user, chosen from the users assignable by the logged-in user
     * @param alertId the alert identifier
     * @param userId the user identifier to assign the alert to
     * @returns Observable of IAlertHandlingRecord
     */
    assignAlertToUser(alertId: string, userId: string): Observable<IAlertHandlingRecord> {
        return super.update(`${API.alertsBaseUrl}/${alertId}/handling/assign-to-user/${userId}`, '' as any) as unknown as Observable<IAlertHandlingRecord>;
    }

    /**
     * removes an alert's assignee
     * @param alertId the alert identifier
     * @returns Observable of IAlertHandlingRecord
     */
    unassignAlert(alertId: string): Observable<IAlertHandlingRecord> {
        return super.update(`${API.alertsBaseUrl}/${alertId}/handling/unassign`, '' as any) as unknown as Observable<IAlertHandlingRecord>;
    }

    /**
     * manually sets an alert's handling status
     * @param alertId the alert identifier
     * @param statusCode the handling status code
     * @returns Observable of IAlertHandlingRecord
     */
    setAlertHandlingStatus(alertId: string, statusCode: string): Observable<IAlertHandlingRecord> {
        return super.update(`${API.alertsBaseUrl}/${alertId}/handling/status/${statusCode}`, '' as any) as unknown as Observable<IAlertHandlingRecord>;
    }
}

const adaptAlert = (data: IAlert): Alert => new Alert(data);
const adaptAlertList = (data): Alert[] => data.map(adaptAlert);

const adaptHandledAlert = (data: IHandledAlert): HandledAlert => new HandledAlert(data);
const adaptHandledAlertList = (data): HandledAlert[] => data.map(adaptHandledAlert);

const adaptSubscriberFeatureState = (data: IFeatureState): FeatureState => new FeatureState(data);
const adaptSubscriberFeatureStatesList = (data): FeatureState[] => data.map(adaptSubscriberFeatureState);
